if (!customElements.get("nmn-purchase-bar")) {
  customElements.define(
    "nmn-purchase-bar",
    class extends HTMLElement {
      connectedCallback() {
        this.disconnect();
        this.controller = new AbortController();
        this.owner = this.closest("nmn-product");
        this.form = this.owner?.querySelector("product-form form");
        this.purchase = this.owner?.querySelector(".nmn-product__purchase");
        this.boundary = this.closest("main");
        if (!this.form || !this.purchase) return;
        this.mainButton = this.form.querySelector('[type="submit"]');
        this.select = this.querySelector("[data-bar-option]");
        this.button = this.querySelector("[data-bar-add]");
        this.error = this.querySelector("[data-bar-error]");
        this.variantSelect = this.form.querySelector("[data-variant-select]");
        this.cards = this.owner.dataset.offerLayout !== "original";
        this.buildOptions();
        const signal = this.controller.signal;
        this.select.addEventListener("change", () => this.selectPurchase(), {
          signal,
        });
        this.button.addEventListener(
          "click",
          () => {
            if (!this.button.disabled) this.mainButton.click();
          },
          { signal },
        );
        this.owner.addEventListener(
          "change",
          () => queueMicrotask(() => this.sync()),
          { signal },
        );
        this.mutations = new MutationObserver(() => this.sync());
        this.mutations.observe(this.purchase, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ["disabled", "aria-disabled", "class", "hidden"],
        });
        this.scheduleVisibility = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.updateVisibility();
          });
        };
        window.addEventListener("scroll", this.scheduleVisibility, {
          passive: true,
          signal,
        });
        window.addEventListener("resize", this.scheduleVisibility, {
          passive: true,
          signal,
        });
        this.resize = new ResizeObserver(this.scheduleVisibility);
        this.resize.observe(this.purchase);
        if (this.boundary) this.resize.observe(this.boundary);
        this.resize.observe(this);
        this.hidden = false;
        this.sync();
        this.updateVisibility();
      }
      buildOptions() {
        this.select.replaceChildren();
        if (this.variantSelect && this.owner.dataset.requiresPlan !== "true") {
          const group = document.createElement("optgroup");
          group.label = "One-time purchase";
          [...this.variantSelect.options].forEach((source) => {
            const option = new Option(
              source.textContent.trim(),
              `once:${source.value}`,
            );
            option.disabled =
              source.disabled || source.dataset.available === "false";
            group.append(option);
          });
          this.select.append(group);
        }
        this.plans = [
          ...this.form.querySelectorAll(
            this.cards ? "[data-offer]" : 'input[name="selling_plan"]',
          ),
        ].filter((input) => input.value);
        if (this.plans.length) {
          const group = document.createElement("optgroup");
          group.label = "Subscription";
          this.plans.forEach((input) => {
            const label = this.cards
              ? input
                  .closest("label")
                  .querySelector(".nmn-product__plan-description")
                  .textContent.trim()
              : input.parentElement.textContent.trim();
            const option = new Option(
              `${label} — ${input.dataset.price}`,
              `plan:${input.value}`,
            );
            option.disabled = input.disabled;
            group.append(option);
          });
          this.select.append(group);
        }
      }
      selectPurchase() {
        const [kind, id] = this.select.value.split(":");
        if (kind === "plan") {
          const input = this.plans.find((plan) => plan.value === id);
          if (!input || input.disabled) return;
          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        } else if (this.variantSelect) {
          this.variantSelect.value = id;
          if (!this.cards) {
            const once = this.form.querySelector(
              'input[name="selling_plan"][value=""]',
            );
            if (once) {
              once.checked = true;
              once.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }
          this.variantSelect.dispatchEvent(
            new Event("change", { bubbles: true }),
          );
        }
        this.sync();
      }
      sync() {
        const data = new FormData(this.form);
        const plan = data.get("selling_plan");
        this.select.value = plan ? `plan:${plan}` : `once:${data.get("id")}`;
        const busy =
          this.mainButton.getAttribute("aria-disabled") === "true" ||
          this.mainButton.classList.contains("loading");
        this.button.disabled = this.mainButton.disabled || busy;
        this.select.disabled = busy;
        this.button.setAttribute("aria-busy", String(busy));
        this.button.textContent = this.mainButton
          .querySelector("[data-submit-label]")
          .textContent.trim();
        const errorWrapper = this.purchase.querySelector(
          ".product-form__error-message-wrapper",
        );
        const message =
          errorWrapper && !errorWrapper.hidden
            ? errorWrapper
                .querySelector(".product-form__error-message")
                ?.textContent.trim()
            : "";
        this.error.textContent = message || "";
        this.error.hidden = !message;
      }
      updateVisibility() {
        // Reserve the bar's space inside the PDP and stop at that boundary,
        // allowing the footer to scroll into view beneath it.
        const height = this.offsetHeight;
        if (this.boundary && height !== this.barHeight) {
          this.barHeight = height;
          this.boundary.style.setProperty(
            "--nmn-purchase-bar-height",
            `${height}px`,
          );
        }
        const bottom =
          this.boundary?.getBoundingClientRect().bottom ?? window.innerHeight;
        this.style.setProperty(
          "--nmn-purchase-bar-offset",
          `${Math.max(0, window.innerHeight - bottom)}px`,
        );
        // Appear only after the offer box has passed above the usable viewport,
        // not while it is still below the fold on a mobile product gallery.
        const header =
          parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--nmn-header-height",
            ),
          ) || 0;
        const visible =
          this.purchase.getBoundingClientRect().bottom <= header && bottom > 0;
        if (!visible && this.contains(document.activeElement))
          this.mainButton.focus({ preventScroll: true });
        this.toggleAttribute("data-visible", visible);
        this.inert = !visible;
      }
      disconnect() {
        this.controller?.abort();
        this.mutations?.disconnect();
        this.resize?.disconnect();
        this.boundary?.style.removeProperty("--nmn-purchase-bar-height");
        this.barHeight = null;
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
      }
      disconnectedCallback() {
        this.disconnect();
      }
    },
  );
}
